/**
 * Search for the signed-in homeowner's Home screen.
 * Callers pass records the homeowner API already returned for this home.
 * Those reads are limited to that homeowner. This file does not query the
 * database and does not add a table.
 */

const { parseLocalDate } = require('../utils/dates');
const { stripVendorListingDisclaimer } = require('./vendorDescription');

const PER_GROUP = 6;
const RECENT_LIMIT = 5;
const NOTHING_FOUND = 'Nothing found';
const SEARCH_PLACEHOLDER = 'Search this home';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const GROUP_DEFS = [
  { key: 'tasks', label: 'Tasks' },
  { key: 'projects', label: 'Projects' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'documents', label: 'Documents' },
  { key: 'details', label: 'Home details' },
  { key: 'places', label: 'App places' },
];

const CONTACT_LABELS = {
  electric: 'Electric',
  water_sewer: 'Water',
  waste: 'Waste',
  gas: 'Gas',
  internet: 'Internet',
  tv: 'TV',
  hoa: 'Homeowners Association',
};

function normalize(value) {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function shortDate(value) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return `${MONTHS[date.getMonth()]} ${date.getDate()}`;
}

function clean(value) {
  return String(value || '').trim();
}

function entry(kind, id, title, subtitle, extra) {
  const label = clean(title);
  if (!label) return null;
  return Object.assign({
    key: `${kind}:${id}`,
    kind,
    id: String(id),
    title: label,
    subtitle: subtitle || kind,
    words: '',
  }, extra || {});
}

function taskTitle(task) {
  return clean(task && (task.task_name || (task.task && task.task.title))) || 'Task';
}

function taskSubtitle(task) {
  if (task && task.completed) return 'Task · Done';
  const due = shortDate(task && task.due_date);
  return due ? `Task · Due ${due}` : 'Task';
}

function projectSubtitle(project) {
  const due = shortDate(project && project.due_date);
  if (due) return `Project · Due ${due}`;
  const tasks = project && Array.isArray(project.tasks) ? project.tasks : [];
  if (!tasks.length) return 'Project';
  const done = tasks.filter((task) => task && task.completed).length;
  return `Project · ${done} of ${tasks.length} done`;
}

function vendorCategory(vendor) {
  if (!vendor) return '';
  if (clean(vendor.category)) return clean(vendor.category);
  if (clean(vendor.trade)) return clean(vendor.trade);
  if (Array.isArray(vendor.categories) && vendor.categories.length) return clean(vendor.categories[0]);
  return '';
}

function flatText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(flatText).filter(Boolean).join(' ');
  if (typeof value === 'object') {
    return Object.keys(value).map((key) => flatText(value[key])).filter(Boolean).join(' ');
  }
  return '';
}

function contactLabel(category) {
  const key = clean(category);
  if (!key) return 'Utility';
  if (CONTACT_LABELS[key]) return CONTACT_LABELS[key];
  return key.split('_').map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : '')).join(' ');
}

function personName(agent) {
  if (!agent) return '';
  return [agent.first_name, agent.last_name].filter(Boolean).join(' ');
}

function pushItem(list, item) {
  if (item) list.push(item);
}

function taskItems(tasks) {
  return (tasks || []).map((task) => {
    if (!task || !task.id) return null;
    const title = taskTitle(task);
    const description = (task.task && task.task.description) || task.description || '';
    const category = (task.task && task.task.category) || '';
    return entry('task', task.id, title, taskSubtitle(task), {
      dueDate: task.due_date || '',
      words: `${description} ${category}`,
    });
  }).filter(Boolean);
}

function projectItems(projects) {
  return (projects || []).map((project) => {
    if (!project || !project.id) return null;
    const tasks = Array.isArray(project.tasks) ? project.tasks : [];
    const taskWords = tasks.map((task) => clean(task && (task.title || task.task_name))).filter(Boolean).join(' ');
    return entry('project', project.id, project.title, projectSubtitle(project), {
      words: `${project.description || ''} ${taskWords}`,
    });
  }).filter(Boolean);
}

function vendorItems(vendors, pros, includeAgentVendors) {
  const saved = (pros || []).map((pro) => {
    if (!pro || !pro.id || !clean(pro.name)) return null;
    const trade = clean(pro.trade);
    return entry('vendor', pro.id, pro.name, trade ? `Vendor · ${trade}` : 'Vendor', {
      source: 'pro',
      words: `${trade} ${pro.phone || ''} ${pro.email || ''}`,
    });
  }).filter(Boolean);
  if (!includeAgentVendors) return saved;
  const listed = (vendors || []).map((vendor) => {
    if (!vendor || !vendor.id || !clean(vendor.name)) return null;
    const category = vendorCategory(vendor);
    const categories = Array.isArray(vendor.categories) ? vendor.categories.join(' ') : '';
    return entry('vendor', vendor.id, vendor.name, category ? `Vendor · ${category}` : 'Vendor', {
      source: 'agent',
      words: `${category} ${categories} ${stripVendorListingDisclaimer(vendor.description)}`,
    });
  }).filter(Boolean);
  return saved.concat(listed);
}

function documentItems(documents) {
  if (!Array.isArray(documents) || documents.length === 0) return [];
  return documents.map((doc) => {
    if (!doc || !doc.id) return null;
    const category = clean(doc.category);
    return entry('document', doc.id, doc.title || doc.file_name, category ? `Document · ${category}` : 'Document', {
      words: `${doc.file_name || ''} ${category}`,
    });
  }).filter(Boolean);
}

function detailItems(input) {
  const client = input.client || {};
  const items = [];
  const street = clean(client.address);
  const cityLine = [client.city, client.state, client.zip_code].filter(Boolean).join(' ');
  if (street) {
    pushItem(items, entry('detail', 'address', street, 'Home detail · Address', {
      tab: 'property',
      anchor: 'home-detail-address',
      words: `${street} ${cityLine}`,
    }));
  }
  const countyName = clean(input.countyName);
  if (countyName) {
    pushItem(items, entry('detail', 'county', countyName, 'Home detail · County', {
      tab: 'property',
      anchor: 'home-detail-address',
      words: countyName,
    }));
  }
  const propertyTypeLabel = clean(input.propertyTypeLabel);
  if (propertyTypeLabel) {
    pushItem(items, entry('detail', 'property-type', propertyTypeLabel, 'Home detail · Property type', {
      tab: 'property',
      anchor: 'home-detail-address',
      words: propertyTypeLabel,
    }));
  }
  (input.features || []).forEach((feature) => {
    if (!feature || !feature.id) return;
    pushItem(items, entry('detail', `feature-${feature.id}`, feature.label, 'Home detail · Feature', {
      tab: 'property',
      anchor: `home-detail-feature-${feature.id}`,
      words: feature.label,
    }));
  });
  (input.systems || []).forEach((system) => {
    if (!system || !system.id) return;
    const data = system.data || {};
    const year = clean(data.install_year || data.installYear || data.year_installed);
    const brand = clean(data.manufacturer || data.name);
    const extra = [brand, year ? `Installed ${year}` : ''].filter(Boolean).join(' · ');
    pushItem(items, entry('detail', `system-${system.id}`, system.name, extra ? `Home detail · ${extra}` : 'Home detail', {
      tab: 'property',
      systemId: system.id,
      anchor: `home-system-${system.id}`,
      words: `${system.name} ${flatText(data)}`,
    }));
  });
  (input.contacts || []).forEach((contact) => {
    if (!contact || !contact.id) return;
    const name = clean(contact.utility_name || contact.name);
    const label = contactLabel(contact.category || contact.custom_category);
    pushItem(items, entry('detail', `contact-${contact.id}`, name, `Home detail · ${label}`, {
      tab: 'contacts',
      anchor: `contact-${contact.id}`,
      words: `${name} ${label} ${contact.phone || ''} ${contact.website || ''}`,
    }));
  });
  [
    ['insurance', 'Home insurance', client.insurance_renewal_date],
    ['flood', 'Flood insurance', client.flood_renewal_date],
    ['warranty', 'Home warranty', client.warranty_renewal_date],
  ].forEach(([id, title, date]) => {
    const when = shortDate(date);
    if (!when) return;
    pushItem(items, entry('detail', id, title, `Home detail · Renews ${when}`, {
      tab: 'property',
      anchor: 'home-detail-plan',
      words: `${title} ${when}`,
    }));
  });
  return items;
}

function place(id, title, words, extra) {
  return entry('place', id, title, 'App place', Object.assign({ words }, extra || {}));
}

function placeItems(input) {
  const agent = input.agent || null;
  const name = personName(agent);
  const sponsorless = input.sponsorless !== false && !agent;
  const items = [
    place('overview', 'Home', 'home'),
    place('tasks', 'Tasks', 'tasks maintenance to do list', { tab: 'tasks' }),
    place('projects', 'Projects', 'projects jobs', { tab: 'projects' }),
    place('vendors', 'Vendors', 'vendors pros service providers', { tab: 'vendors' }),
    place('documents', 'Documents', 'documents files records', { tab: 'documents' }),
    place('property', 'Property Details', 'property details house', { tab: 'property' }),
    place('contacts', 'Utility and Service Providers', 'utilities providers contacts electric water', { tab: 'contacts' }),
    place('assistant', 'Home assistant', 'ai assistant questions', { tab: 'assistant' }),
    place('more', 'More', 'more account', { tab: 'settings' }),
    place('notifications', 'Notification Settings', 'notifications alerts reminders settings', { tab: 'settings', pane: 'notifications' }),
    place('sponsor', 'Sponsor code', `sponsor code ${name}`, { tab: 'settings', pane: 'sponsor' }),
    place('legal', 'Legal', 'legal terms privacy policy', { place: 'legal' }),
    place('appearance', 'Appearance', 'appearance dark mode light mode theme', { tab: 'settings', place: 'appearance', anchor: 'appearance-settings' }),
    place('home-history', 'Home History Report', 'pacarion home history report maintenance history', { tab: 'home-history' }),
    place('replay-tour', 'Replay tour', 'tour walkthrough replay', { place: 'replay-tour' }),
  ];
  if (sponsorless) {
    items.push(place('agent', 'Pacarion', 'pacarion', { tab: 'agent' }));
  } else if (agent) {
    const office = [agent.team_name, agent.brokerage].filter(Boolean).join(' ');
    items.push(place('agent', 'Sponsor', `sponsor ${name} ${office}`, { tab: 'agent' }));
  }
  if (!input.readOnly) {
    items.push(place('move', 'Move to a new home', 'move new home', { tab: 'settings', place: 'move' }));
  }
  items.forEach((item) => {
    if (item && !item.tab && !item.place && !item.pane) item.tab = item.id;
  });
  return items.filter(Boolean);
}

function buildHomeSearchCatalog(input) {
  const source = input || {};
  const agent = source.agent || null;
  return {
    tasks: taskItems(source.tasks),
    projects: projectItems(source.projects),
    vendors: vendorItems(source.vendors, source.pros, source.includeAgentVendors !== false),
    documents: documentItems(source.documents),
    details: detailItems(source),
    places: placeItems({
      agent,
      sponsorless: source.sponsorless != null ? source.sponsorless : !agent,
      readOnly: source.readOnly === true,
    }),
  };
}

function scoreItem(query, item) {
  const title = normalize(item && item.title);
  const blob = normalize(`${item && item.title} ${item && item.words}`);
  if (!query || !blob) return 0;
  if (title.startsWith(query)) return 400;
  if (title.includes(query)) return 300;
  const words = query.split(' ').filter(Boolean);
  if (words.length > 1 && words.every((word) => blob.includes(word))) {
    return words.every((word) => title.includes(word)) ? 200 : 100;
  }
  if (blob.includes(query)) return 150;
  return 0;
}

function searchHome(catalog, query) {
  const q = normalize(query).slice(0, 80);
  if (!q) return [];
  const source = catalog || {};
  const groups = [];
  GROUP_DEFS.forEach((group) => {
    const items = (source[group.key] || [])
      .map((item) => ({ item, score: scoreItem(q, item) }))
      .filter((row) => row.score > 0)
      .sort((left, right) => right.score - left.score || left.item.title.localeCompare(right.item.title))
      .slice(0, PER_GROUP)
      .map((row) => row.item);
    if (items.length) groups.push({ id: group.key, label: group.label, items });
  });
  return groups;
}

function recentSearchKey(homeId) {
  return `pacarion_recent_home_search_${homeId || 'home'}`;
}

function readRecentSearches(storage, homeId) {
  if (!storage || typeof storage.getItem !== 'function' || !homeId) return [];
  try {
    const parsed = JSON.parse(storage.getItem(recentSearchKey(homeId)) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => clean(item)).filter(Boolean).slice(0, RECENT_LIMIT);
  } catch (error) {
    return [];
  }
}

function rememberRecentSearch(storage, homeId, query) {
  const text = clean(query);
  const current = readRecentSearches(storage, homeId);
  if (!storage || typeof storage.setItem !== 'function' || !homeId || text.length < 2) return current;
  const next = [text].concat(current.filter((item) => item.toLowerCase() !== text.toLowerCase())).slice(0, RECENT_LIMIT);
  try {
    storage.setItem(recentSearchKey(homeId), JSON.stringify(next));
  } catch (error) {
    return current;
  }
  return next;
}

module.exports = {
  NOTHING_FOUND,
  SEARCH_PLACEHOLDER,
  GROUP_DEFS,
  buildHomeSearchCatalog,
  searchHome,
  readRecentSearches,
  rememberRecentSearch,
  recentSearchKey,
};
